import React from "react";
import { Link } from "react-router-dom";
import PolicyPage, { SupportEmail } from "./PolicyPage";

export function PaymentSecurityPolicy() {
  return (
    <PolicyPage title="Payment & Security Policy">
      <p>
        <strong>Accepted payment and currency</strong>
        <br />
        Checkout displays the payment methods currently available for your
        order. Unless the checkout expressly says otherwise, prices and charges
        are shown and processed in United States dollars (USD). Your bank may
        apply currency-conversion or international-transaction fees that we do
        not control.
      </p>
      <p>
        <strong>Secure checkout</strong>
        <br />
        Card information is entered on a payment processor&apos;s hosted checkout
        and is not intended to be stored directly by this storefront. The
        processor may use encryption, tokenization, fraud screening, or
        additional authentication. Never email us a full card number, security
        code, password, or online-banking credential.
      </p>
      <p>
        <strong>Authorization and order acceptance</strong>
        <br />
        A successful payment authorization does not by itself mean that an
        order has been accepted. We may review availability, price, delivery
        restrictions, and fraud indicators before acceptance. A temporary
        authorization hold may appear even if an order is later declined or
        cancelled; the issuing bank controls when that hold is released.
      </p>
      <p>
        <strong>Verification and fraud prevention</strong>
        <br />
        We or our payment provider may request billing verification, identity
        confirmation, or additional authentication. We may limit, pause, or
        cancel transactions reasonably believed to be unauthorized, fraudulent,
        intended for resale contrary to stated limits, or otherwise unlawful.
      </p>
      <p>
        <strong>Declined, duplicate, or incorrect charges</strong>
        <br />
        A payment may be declined by the issuer or processor for reasons we
        cannot see. Before retrying, check whether a pending authorization
        already exists. If you believe you were charged incorrectly, contact us
        with the order number, date, amount, and the last four digits of the
        payment method. Do not send complete account details.
      </p>
      <p>
        <strong>Refunds and chargebacks</strong>
        <br />
        Approved refunds are returned to the original payment method where
        possible and follow our <Link to="/returns">Refund Policy</Link>. Please
        contact us before opening a payment dispute so we can investigate. This
        request does not limit any right to dispute a transaction under
        applicable law or card-network rules.
      </p>
      <p>
        <strong>Payment data and support</strong>
        <br />
        Payment providers process transaction information under their own
        privacy terms. Our use of order and payment-status information is
        described in the <Link to="/privacy">Privacy Policy</Link>. For payment
        support, email <SupportEmail />.
      </p>
    </PolicyPage>
  );
}

export function OrderCancellationPolicy() {
  return (
    <PolicyPage title="Orders & Cancellations Policy">
      <p>
        <strong>Order acknowledgement and acceptance</strong>
        <br />
        An order submission or payment receipt is an acknowledgement, not final
        acceptance. A binding sale is formed when we expressly accept the order
        or dispatch the item, depending on the confirmation we send. Keep your
        order number for all support requests.
      </p>
      <p>
        <strong>Reviewing your order</strong>
        <br />
        Check the item, material, size, quantity, delivery address, and contact
        details before paying. Contact us immediately if anything is incorrect.
        We cannot guarantee a change after production, personalization, packing,
        or dispatch has started.
      </p>
      <p>
        <strong>Customer cancellation requests</strong>
        <br />
        You may request cancellation before dispatch. Ready-to-ship orders can
        often be cancelled if fulfillment has not begun. Custom, altered,
        resized, engraved, or made-to-order pieces may not be cancellable after
        materials have been committed or work has started. Any mandatory
        cancellation right under applicable consumer law remains unaffected.
      </p>
      <p>
        <strong>Changes to an accepted order</strong>
        <br />
        A requested change may require a revised price, production estimate, or
        delivery charge. We will seek your approval before proceeding with a
        material change. An address change is not effective until confirmed in
        writing and may not be possible after a shipping label is created.
      </p>
      <p>
        <strong>When we may cancel</strong>
        <br />
        We may cancel and refund an order because an item or material is
        unavailable, a price or description contains a material error, payment
        cannot be verified, delivery is legally or operationally unavailable,
        we reasonably suspect fraud, or fulfilling the order would violate law
        or another person&apos;s rights.
      </p>
      <p>
        <strong>Quantity limits and resale</strong>
        <br />
        We may apply reasonable quantity limits or reject orders that appear to
        be placed by automated means or for unauthorized commercial resale.
        Separate wholesale arrangements require our written agreement.
      </p>
      <p>
        <strong>How to request a change or cancellation</strong>
        <br />
        Email <SupportEmail /> with “Order change” or “Cancellation” in the
        subject line and include your order number. A request is complete only
        when we confirm it. Refund timing is governed by our{" "}
        <Link to="/returns">Refund Policy</Link>.
      </p>
    </PolicyPage>
  );
}

export function WarrantyRepairsPolicy() {
  return (
    <PolicyPage title="Warranty & Repairs Policy">
      <p>
        <strong>Statutory rights</strong>
        <br />
        This policy is in addition to rights and remedies that cannot be
        excluded under applicable consumer law. If a product page, written
        quotation, or order confirmation includes a separate commercial
        warranty, that warranty applies on its stated terms.
      </p>
      <p>
        <strong>Workmanship concerns</strong>
        <br />
        If you believe an item has a manufacturing or workmanship defect,
        contact us promptly with the order number, a description, and clear
        photographs. We may need to inspect the item before deciding whether a
        defect is present and which remedy is appropriate.
      </p>
      <p>
        <strong>What is not a workmanship defect</strong>
        <br />
        Normal wear, scratches, tarnish, plating wear, accidental damage,
        impact, bent settings, stretched chains, lost stones caused by damage,
        improper storage, chemical exposure, failure to follow care guidance,
        or work performed by an unauthorized third party are not workmanship
        defects. Natural variation and disclosed vintage condition are also not
        defects.
      </p>
      <p>
        <strong>Assessment and remedies</strong>
        <br />
        After assessment, an eligible claim may be resolved by repair,
        replacement, price reduction, or refund, as appropriate and as required
        by law. A replacement may differ slightly where natural materials or a
        one-of-a-kind vintage item cannot be matched exactly.
      </p>
      <p>
        <strong>Paid repairs and maintenance</strong>
        <br />
        We may be able to quote for repair, resizing, refinishing, cleaning, or
        stone tightening outside an applicable warranty. Availability, cost,
        and timing are confirmed only after assessment. Do not send an item
        until you receive written shipping instructions.
      </p>
      <p>
        <strong>Shipping and third-party work</strong>
        <br />
        Responsibility for assessment shipping depends on the outcome and any
        mandatory law. Use tracked, adequately insured shipping. Unauthorized
        repair or alteration may prevent us from determining the original cause
        of a problem and may affect a voluntary warranty.
      </p>
      <p>
        <strong>Requesting service</strong>
        <br />
        Email <SupportEmail /> with your order details and photographs. For
        immediate care and wear guidance, review our{" "}
        <Link to="/care-safety">Product Care &amp; Safety Policy</Link>.
      </p>
    </PolicyPage>
  );
}

export function ProductCareSafetyPolicy() {
  return (
    <PolicyPage title="Product Care & Safety Policy">
      <p>
        <strong>Everyday care</strong>
        <br />
        Jewelry is vulnerable to impact, abrasion, heat, moisture, and
        chemicals. Remove pieces before sleeping, bathing, swimming, exercising,
        household cleaning, or manual work. Apply perfume, lotion, cosmetics,
        and hair products before putting jewelry on.
      </p>
      <p>
        <strong>Cleaning</strong>
        <br />
        Wipe pieces gently with a clean, soft, dry cloth after wear. Do not use
        abrasive polish, ultrasonic equipment, steam, bleach, acids, or jewelry
        dip unless a qualified professional confirms it is safe for every
        material in the piece.
      </p>
      <p>
        <strong>Natural and porous materials</strong>
        <br />
        Pearl, shell, turquoise, coral-like materials, opal, wood, enamel, and
        other delicate or porous materials may be damaged by soaking, sudden
        temperature changes, chemicals, or ultrasonic cleaning. Their natural
        color, grain, or surface may change gradually with use.
      </p>
      <p>
        <strong>Storage and inspection</strong>
        <br />
        Store each piece separately in a dry, soft-lined place. Fasten chains
        before storage and keep jewelry away from direct sunlight and humidity.
        Regularly inspect clasps, links, prongs, and settings. Stop wearing an
        item if a component is loose, bent, sharp, or damaged.
      </p>
      <p>
        <strong>Fit, allergies, and safety</strong>
        <br />
        Product materials are described to the best of our knowledge, but no
        item can be guaranteed suitable for every sensitivity. Stop use and seek
        appropriate medical advice if irritation occurs. Jewelry contains small
        parts and is not a toy; keep it away from children and pets. Long chains
        may present an entanglement risk.
      </p>
      <p>
        <strong>Professional service</strong>
        <br />
        Fine jewelry benefits from periodic professional inspection, especially
        pieces worn daily. Vintage or structurally delicate items may require
        specialist care. Contact us before arranging a repair that may affect an
        applicable warranty.
      </p>
      <p>
        <strong>Questions</strong>
        <br />
        Care varies by material and construction. For item-specific guidance,
        send the product name or order number to <SupportEmail />.
      </p>
    </PolicyPage>
  );
}

export function CustomOrdersPolicy() {
  return (
    <PolicyPage title="Custom & Made-to-Order Policy">
      <p>
        <strong>Scope</strong>
        <br />
        This policy applies to bespoke designs, commissions, engraving,
        resizing, alterations, personalization, and items produced after an
        order is placed. A written quotation or order confirmation may add
        project-specific terms; if it conflicts with this general policy, the
        more specific written term controls to the extent permitted by law.
      </p>
      <p>
        <strong>Consultation and quotation</strong>
        <br />
        Tell us the desired materials, size, timing, and budget. A discussion,
        sketch, estimate, or availability check is not a commitment to produce.
        Work begins only after the design scope, price, payment schedule, and
        estimated timeline are confirmed in writing.
      </p>
      <p>
        <strong>Approvals and changes</strong>
        <br />
        You are responsible for reviewing names, dates, sizes, spelling,
        measurements, and design approvals. Changes requested after approval may
        be unavailable or may change the price and delivery estimate. We will
        obtain approval before proceeding with a material additional charge.
      </p>
      <p>
        <strong>Natural variation and tolerances</strong>
        <br />
        Handmade work and natural materials cannot be identical to a screen
        image, sample, sketch, or previous piece. Reasonable differences in
        color, pattern, texture, dimensions, weight, and hand finishing are part
        of the process and are not defects.
      </p>
      <p>
        <strong>Customer-provided ideas and materials</strong>
        <br />
        Only submit material you created or are authorized to use. We will not
        knowingly reproduce another brand&apos;s protected logo, artwork, or design
        without permission. We may decline a request that could infringe rights,
        mislead buyers, create a safety concern, or violate law. Acceptance of
        heirloom stones or components, if offered, requires separate written
        terms after inspection.
      </p>
      <p>
        <strong>Timing, cancellation, and returns</strong>
        <br />
        Production estimates are not guaranteed and can change with approvals,
        material availability, or technical issues. Because custom work is made
        for you, it may become non-cancellable and non-returnable after materials
        are committed or work begins, except for defects, errors on our part, or
        rights that cannot legally be excluded.
      </p>
      <p>
        <strong>Design rights and confidentiality</strong>
        <br />
        You retain rights in original materials you provide. You grant us a
        limited permission to use those materials only to evaluate, communicate
        about, and fulfill the project. Ownership or permitted reuse of any new
        custom design will be stated in the written quotation; we will not imply
        transfer of your pre-existing rights.
      </p>
      <p>
        <strong>Custom inquiries</strong>
        <br />
        Send project details to <SupportEmail />. Do not send irreplaceable
        physical materials until we provide written instructions.
      </p>
    </PolicyPage>
  );
}

export function PromotionsPricingPolicy() {
  return (
    <PolicyPage title="Pricing & Promotions Policy">
      <p>
        <strong>Displayed prices</strong>
        <br />
        Prices are shown in the currency identified on the site or at checkout
        and may change before an order is accepted. Unless expressly included,
        prices do not include delivery, import duty, brokerage, VAT, sales tax,
        or other destination charges.
      </p>
      <p>
        <strong>Pricing or description errors</strong>
        <br />
        We try to keep prices and product information accurate. If a material
        error is discovered before acceptance, we may correct it and ask whether
        you wish to proceed at the corrected price, or cancel and refund the
        order. We will not knowingly use misleading reference prices.
      </p>
      <p>
        <strong>Promotion terms</strong>
        <br />
        A promotion may have a stated start and end time, eligibility rule,
        territory, product exclusion, minimum spend, redemption limit, or code.
        Those specific terms form part of this policy. Promotions are subject to
        availability and have no cash value unless required by law.
      </p>
      <p>
        <strong>Combining offers</strong>
        <br />
        Only one promotional code may be used per order unless the offer says it
        can be combined. Shipping offers, gifts with purchase, custom services,
        repairs, vintage pieces, or previously reduced items may be excluded
        where stated.
      </p>
      <p>
        <strong>Returns from promotional orders</strong>
        <br />
        Refunds are based on the amount actually paid for the returned item.
        Where a multi-item discount or gift depended on a purchase threshold,
        the refund may be recalculated and an unreturned promotional benefit may
        be deducted where lawful and disclosed.
      </p>
      <p>
        <strong>Price adjustments</strong>
        <br />
        We do not offer retroactive price adjustments unless a promotion
        expressly provides one or applicable law requires it. Separate orders
        cannot be combined after submission to qualify for a threshold.
      </p>
      <p>
        <strong>Fair use</strong>
        <br />
        We may refuse, reverse, or cancel a promotional benefit obtained through
        automation, code publication contrary to its terms, multiple identities,
        payment abuse, or other manipulation. Questions can be sent to{" "}
        <SupportEmail />.
      </p>
    </PolicyPage>
  );
}

export function IntellectualPropertyPolicy() {
  return (
    <PolicyPage title="Intellectual Property Policy">
      <p>
        <strong>Our approach</strong>
        <br />
        Tuddlepetgoods respects intellectual-property rights and expects site
        users, suppliers, and collaborators to do the same. We aim to publish
        only branding, text, product imagery, graphics, and designs that we own,
        created independently, licensed, or are otherwise legally permitted to
        use.
      </p>
      <p>
        <strong>Site content</strong>
        <br />
        Except for material identified as belonging to a third party, the site
        content is protected by applicable copyright, trademark, design, and
        other laws. Access to the site does not transfer ownership. You may view
        it and make ordinary personal-shopping use, but may not reproduce,
        scrape, republish, sell, remove rights notices from, or commercially
        exploit content without authorization.
      </p>
      <p>
        <strong>Third-party rights</strong>
        <br />
        Third-party names, marks, and content remain the property of their
        respective owners. Their appearance does not imply sponsorship or
        endorsement. We do not grant permission to use any third-party right.
      </p>
      <p>
        <strong>Material you send us</strong>
        <br />
        You confirm that you have the right to submit reference images, text,
        logos, or designs. You grant us only the non-exclusive permission needed
        to review your request, communicate with you, and fulfill an authorized
        order. We may decline or stop work where ownership or permission is
        unclear.
      </p>
      <p>
        <strong>Reporting suspected infringement</strong>
        <br />
        Send a notice to <SupportEmail /> identifying the protected work or
        right, the exact site URL and material at issue, your contact details,
        the basis for your ownership or authority, and a good-faith explanation.
        Include a signed statement that the information is accurate. Do not send
        sensitive identity documents unless we request a secure method.
      </p>
      <p>
        <strong>Review process</strong>
        <br />
        We may temporarily restrict material while reviewing a sufficiently
        detailed notice, request clarification, contact the relevant supplier or
        contributor, preserve necessary records, and remove or replace material
        where appropriate. We may share the substance of a notice with the
        affected party when lawful and necessary for a fair review.
      </p>
      <p>
        <strong>False or abusive notices</strong>
        <br />
        Do not knowingly make a false claim or misuse this process to suppress
        lawful competition or criticism. Nothing in this policy limits lawful
        exceptions, fair dealing, fair use where applicable, or other rights
        that cannot be waived.
      </p>
    </PolicyPage>
  );
}

export function AccessibilityStatement() {
  return (
    <PolicyPage title="Accessibility Statement">
      <p>
        <strong>Our commitment</strong>
        <br />
        Tuddlepetgoods wants customers with different abilities, devices, and
        assistive technologies to be able to browse products, understand store
        policies, and request support. Accessibility is an ongoing practice, not
        a one-time certification.
      </p>
      <p>
        <strong>Our accessibility aim</strong>
        <br />
        We aim to improve the site with reference to generally recognized web
        accessibility practices, including relevant WCAG guidance. This is an
        improvement target and not a claim that every page or third-party
        service currently satisfies every success criterion.
      </p>
      <p>
        <strong>Using the site</strong>
        <br />
        The site is designed to support keyboard navigation, readable text,
        descriptive labels, meaningful headings, and reduced-motion preferences
        where practical. Browser zoom, text-to-speech, voice control, or other
        assistive settings may also help with access.
      </p>
      <p>
        <strong>Third-party services</strong>
        <br />
        Hosted payment, delivery tracking, maps, or other third-party features
        may have accessibility limitations outside our direct control. We will
        try to offer a reasonable support alternative when you tell us about a
        barrier.
      </p>
      <p>
        <strong>Alternative formats and shopping support</strong>
        <br />
        You may request policy information in a simpler electronic format or
        ask for reasonable help completing a purchase. We will never ask you to
        send full card credentials by email.
      </p>
      <p>
        <strong>Feedback</strong>
        <br />
        Email <SupportEmail /> with the page URL, a description of the barrier,
        the browser or assistive technology used if you are comfortable sharing
        it, and the format or solution you need. We will review good-faith
        feedback and prioritize issues that block essential shopping tasks.
      </p>
    </PolicyPage>
  );
}

export function ComplaintsPolicy() {
  return (
    <PolicyPage title="Complaints & Dispute Resolution">
      <p>
        <strong>Our objective</strong>
        <br />
        We aim to handle complaints fairly, respectfully, and without charging
        a complaint fee. Raising a complaint does not reduce any consumer,
        privacy, payment, or other legal right available to you.
      </p>
      <p>
        <strong>How to complain</strong>
        <br />
        Email <SupportEmail /> with “Complaint” in the subject line. Include your
        name, order number if applicable, a clear description, relevant dates,
        the outcome you seek, and supporting photographs or correspondence. Do
        not include complete payment-card or government-identity numbers.
      </p>
      <p>
        <strong>Our review</strong>
        <br />
        We will acknowledge the complaint as soon as reasonably practical,
        assess the information, and ask focused follow-up questions if needed.
        We aim to provide a substantive response within 14 business days, but a
        complex delivery, payment, safety, or third-party investigation may take
        longer. We will update you if additional time is needed.
      </p>
      <p>
        <strong>Possible outcomes</strong>
        <br />
        Depending on the facts and applicable policy, an outcome may include an
        explanation, correction, repair, replacement, refund, cancellation,
        accessibility adjustment, removal of content, or no further action. We
        will explain the main reason for our decision where appropriate.
      </p>
      <p>
        <strong>Escalation</strong>
        <br />
        If you disagree with the response, reply with “Escalation request” and
        identify any fact or document you believe was overlooked. We will arrange
        a fresh internal review where reasonably possible.
      </p>
      <p>
        <strong>External options</strong>
        <br />
        You may contact an applicable consumer-protection, privacy, payment, or
        dispute-resolution body at any time. We do not require private
        arbitration unless you separately agree to it after a dispute arises.
        The governing-law section in our <Link to="/terms">Terms of Service</Link>
        applies subject to mandatory local consumer rights.
      </p>
      <p>
        <strong>Records and privacy</strong>
        <br />
        Complaint information is used to investigate, respond, prevent repeat
        issues, and comply with law. Access is limited to people and providers
        who need it, as described in our <Link to="/privacy">Privacy Policy</Link>.
      </p>
    </PolicyPage>
  );
}

export function WebsiteDisclaimer() {
  return (
    <PolicyPage title="Website Disclaimer">
      <p>
        <strong>General information</strong>
        <br />
        Website content is provided for general shopping and informational
        purposes. We try to keep it useful and accurate, but content may contain
        omissions or become outdated. Specific order confirmations and written
        quotations control over general website content for that transaction.
      </p>
      <p>
        <strong>Product appearance and availability</strong>
        <br />
        Screen settings, lighting, image scale, handmade work, and natural or
        vintage materials can cause an item to differ from an image. Dimensions
        and weights may be approximate unless expressly guaranteed. Displaying
        a product does not guarantee stock or acceptance of an order.
      </p>
      <p>
        <strong>No professional advice</strong>
        <br />
        Care, sizing, material, gem, styling, customs, tax, and safety information
        is general and is not medical, legal, tax, appraisal, investment, or
        other professional advice. Seek a qualified professional when advice is
        needed for your circumstances.
      </p>
      <p>
        <strong>Third-party content and links</strong>
        <br />
        A link or reference to a payment provider, carrier, social platform, or
        other third party is supplied for convenience and does not mean we
        endorse all of its content. Third parties control their own services,
        security, availability, and terms.
      </p>
      <p>
        <strong>Site availability</strong>
        <br />
        We do not promise uninterrupted or error-free access. We may suspend or
        change site functions for maintenance, security, legal, or operational
        reasons. Use reasonable device security and do not rely on the website
        as the sole storage location for important information.
      </p>
      <p>
        <strong>Mandatory rights</strong>
        <br />
        Nothing in this disclaimer excludes liability or a warranty, guarantee,
        remedy, or other right that cannot lawfully be excluded. Contractual
        limitations are set out in our <Link to="/terms">Terms of Service</Link>.
      </p>
      <p>
        <strong>Corrections</strong>
        <br />
        To report a material error, safety issue, or potentially unauthorized
        content, email <SupportEmail /> with the relevant page URL and details.
      </p>
    </PolicyPage>
  );
}

export function ProductInformationPolicy() {
  return (
    <PolicyPage title="Product Information Policy">
      <p>
        <strong>Descriptions and materials</strong>
        <br />
        We describe products and materials in good faith using information
        reasonably available to us. The individual product page and any written
        order confirmation state what is included. Packaging, props, stands, or
        styling items shown in photographs are not included unless listed.
      </p>
      <p>
        <strong>Natural, handmade, and vintage variation</strong>
        <br />
        Stones, shell, pearl, wood, patina, and other natural or vintage
        materials vary in color, pattern, inclusions, grain, surface, and wear.
        Handmade finishing may also vary slightly. Disclosed characteristics and
        reasonable natural variation are not defects.
      </p>
      <p>
        <strong>Images, color, and scale</strong>
        <br />
        We aim for accurate images, but device displays and lighting affect
        color. Close-up images can make items appear larger than life. Review
        listed measurements and contact us if scale, fit, or color is important
        to your decision.
      </p>
      <p>
        <strong>Measurements, weight, and fit</strong>
        <br />
        Measurements and weights may be rounded and are subject to reasonable
        production tolerance. Jewelry fit depends on body measurement, design,
        width, and personal preference. A nominal size does not guarantee the
        same fit across every design.
      </p>
      <p>
        <strong>Vintage condition</strong>
        <br />
        Vintage-inspired items are newly made unless identified as vintage.
        Genuine vintage or previously owned pieces may show age, repairs,
        replacement components, patina, or wear. Material condition known to us
        will be described or shown where reasonably possible.
      </p>
      <p>
        <strong>Gem, hallmark, certificate, and valuation statements</strong>
        <br />
        A laboratory report, hallmark, appraisal, provenance record, or
        independent certification is included only when the product description
        expressly says so. Marketing descriptions are not a financial valuation
        or a guarantee of resale value.
      </p>
      <p>
        <strong>Sensitivities and suitability</strong>
        <br />
        Even materials commonly described as suitable for sensitive skin can
        affect individuals differently. Product information is not a medical
        guarantee. Review our <Link to="/care-safety">Care &amp; Safety Policy</Link>
        and stop use if irritation occurs.
      </p>
      <p>
        <strong>Questions or corrections</strong>
        <br />
        Ask product-specific questions before ordering by emailing{" "}
        <SupportEmail />. If a delivered product materially differs from the
        accepted description, contact us under the{" "}
        <Link to="/returns">Refund Policy</Link>.
      </p>
    </PolicyPage>
  );
}

export function PrivacyChoicesPolicy() {
  return (
    <PolicyPage title="Privacy Choices">
      <p>
        <strong>Available choices</strong>
        <br />
        Depending on your location and the circumstances, you may be able to ask
        for access to personal information, correction, deletion, restriction,
        portability, or information about how it is used and disclosed. You may
        also object to certain processing or withdraw consent where consent is
        the applicable basis.
      </p>
      <p>
        <strong>Marketing and browser storage</strong>
        <br />
        You can opt out of marketing using the method in the message or by
        contacting us. Transactional messages about an order may still be sent.
        Browser controls can clear cookies and local storage; see the{" "}
        <Link to="/cookies">Cookie Policy</Link> for the effect on the cart and
        checkout experience.
      </p>
      <p>
        <strong>Sale, sharing, and targeted advertising</strong>
        <br />
        We do not currently sell personal information for money or use this
        storefront to share personal information for cross-context behavioral
        advertising. If those practices change, we will update our notices and
        provide any choice required by applicable law before the change applies.
      </p>
      <p>
        <strong>Making a request</strong>
        <br />
        Email <SupportEmail /> with “Privacy request” in the subject line and
        describe the right you wish to exercise. Use the email address connected
        with your interaction where possible. An authorized agent may submit a
        request where permitted, but we may ask for proof of authorization and
        direct identity confirmation.
      </p>
      <p>
        <strong>Verification and limits</strong>
        <br />
        We will request only information reasonably needed to verify the request
        and prevent unauthorized disclosure. A request may be limited or refused
        where law permits, including when records must be retained for tax,
        security, fraud-prevention, warranty, legal-claim, or transaction
        purposes. We will explain an applicable reason.
      </p>
      <p>
        <strong>Response and appeal</strong>
        <br />
        We will respond within the period required by applicable law. If you
        disagree with a decision, reply with “Privacy appeal” and explain the
        issue. You may also complain to the privacy or data-protection authority
        available in your location.
      </p>
      <p>
        <strong>No discrimination</strong>
        <br />
        We will not unlawfully discriminate against you for exercising a valid
        privacy right. Full details about collection, purposes, providers,
        retention, and transfers appear in our{" "}
        <Link to="/privacy">Privacy Policy</Link>.
      </p>
    </PolicyPage>
  );
}
